import { useState, type ChangeEventHandler } from "react";
import { Eye, EyeOff } from "lucide-react";
import { FieldShell } from "./FieldShell";
import { fieldIds, inputClass, resolveFieldState } from "./fieldStyles";

export type Mask = "ssn";

const SSN_DIGITS = 9;

interface MaskedInputProps {
  id: string;
  label: string;
  /** Digits only; formatting is applied on display. */
  value: string;
  onChange: (digits: string) => void;
  mask: Mask;
  /** Existing masked value from the API, shown when no new digits were typed. */
  storedMasked?: string | null;
  error?: string | null;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
  children?: never;
}

function formatSsn(digits: string): string {
  const d = digits.slice(0, SSN_DIGITS);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** Masks every digit except the last four, keeping the separators. */
function obscureSsn(digits: string): string {
  const formatted = formatSsn(digits);
  let digitsAfter = digits.length;
  return formatted
    .split("")
    .map((char) => {
      if (!/\d/.test(char)) return char;
      digitsAfter -= 1;
      return digitsAfter >= 4 ? "*" : char;
    })
    .join("");
}

/**
 * SSN entry with a masked display and a show/hide toggle. Edits are derived by
 * diffing the new text against the last display, so the mask characters are never
 * mistaken for user input and hidden digits are never lost.
 */
export function MaskedInput({ id, label, value, onChange, mask, storedMasked, error, hint, required, containerClassName }: MaskedInputProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const ids = fieldIds(id, error, hint);
  const state = resolveFieldState({ error });
  const hasTyped = value !== "";
  const display = hasTyped ? (isRevealed ? formatSsn(value) : obscureSsn(value)) : (storedMasked ?? "");

  /**
   * The caret always sits at the end of the controlled value, so an edit is one of:
   * an append (typed or pasted digits), a trailing deletion (Backspace), or a full
   * replacement (select all, then type). Anything else is ignored rather than guessed.
   */
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const raw = event.currentTarget.value;
    if (raw.startsWith(display)) {
      const appended = raw.slice(display.length).replace(/\D/g, "");
      if (appended !== "") onChange((value + appended).slice(0, SSN_DIGITS));
      return;
    }
    if (display.startsWith(raw)) {
      const removedDigits = display.slice(raw.length).replace(/[^\d*]/g, "").length;
      onChange(value.slice(0, Math.max(0, value.length - removedDigits)));
      return;
    }
    if (!raw.includes("*")) onChange(raw.replace(/\D/g, "").slice(0, SSN_DIGITS));
  };

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName}>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={display}
          onChange={handleChange}
          placeholder={mask === "ssn" ? "XXX-XX-XXXX" : undefined}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={ids.describedBy}
          className={`${inputClass(state)} pr-12 font-mono`}
        />
        <button
          type="button"
          onClick={() => setIsRevealed((current) => !current)}
          aria-pressed={isRevealed}
          aria-label={isRevealed ? "Hide SSN" : "Show SSN"}
          title={isRevealed ? "Hide SSN" : "Show SSN"}
          disabled={!hasTyped}
          className="absolute inset-y-0 right-0 flex min-w-[44px] items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded-r-lg"
        >
          {isRevealed ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </button>
      </div>
    </FieldShell>
  );
}
